import { useState, useEffect } from 'react';
import '../styles/Settings.css';
import YesNoPopup from '../common/components/YesNoPopup';
import { SETTINGS_API } from '../common/ResourcePaths';
import AuthData from '../common/AuthData';
import Fetch from '../services/fetchService';

function AdvancedColors({ loader, setLoader, palette, onSave, onColorChange, setError, setSuccess, setSuccessTimer }) {
    const [modified, setModified] = useState(false);
    const [showPopup, setShowPopup] = useState(false);

    // Color states for CSS variables
    const [dashboardBackgroundColor, setDashboardBackgroundColor] = useState('');
    const [tileBackgroundColor, setTileBackgroundColor] = useState('');
    const [headerBackgroundColor, setHeaderBackgroundColor] = useState('');
    const [dashboardHeaderColor, setDashboardHeaderColor] = useState('');
    const [basketBackgroundColor, setBasketBackgroundColor] = useState('');
    const [basketBackgroundColorInnerCircle, setBasketBackgroundColorInnerCircle] = useState('');
    const [basketIconColor, setBasketIconColor] = useState('');
    const [basketCountBackgroundColor, setBasketCountBackgroundColor] = useState('');
    const [basketCountNumberColor, setBasketCountNumberColor] = useState('');
    const [fontColor, setFontColor] = useState('');
    // New granular color options
    const [categoryButtonTextColor, setCategoryButtonTextColor] = useState('');
    const [productDescriptionColor, setProductDescriptionColor] = useState('');
    const [priceColor, setPriceColor] = useState('');
    const [buttonTextColor, setButtonTextColor] = useState('');

    useEffect(() => {
        if (palette && palette.colors && Array.isArray(palette.colors)) {
            // Map palette colors to all color fields (matching BasicColors logic)
            const headerColor = palette.colors[2] || '#1976d2';
            const accentColor = palette.preview?.accent || headerColor;
            const fontColor = palette.colors[4] || '#1F2937';
            
            setDashboardBackgroundColor(palette.colors[0] || '#F5F5F5');
            setTileBackgroundColor(palette.colors[1] || '#FFFFFF');
            setHeaderBackgroundColor(headerColor);
            setDashboardHeaderColor(palette.colors[3] || '#FFFFFF');
            setFontColor(fontColor);
            
            // Set additional granular colors based on theme
            setCategoryButtonTextColor(headerColor);
            setProductDescriptionColor('#6B7280');
            setPriceColor(headerColor);
            setButtonTextColor('#FFFFFF');
            
            // Set basket colors
            setBasketBackgroundColor(headerColor);
            setBasketBackgroundColorInnerCircle(accentColor);
            setBasketIconColor('#FFFFFF');
            setBasketCountBackgroundColor('#E53E3E');
            setBasketCountNumberColor('#FFFFFF');
            
            setModified(true);
        }
    }, [palette]);

    // Original values for reset functionality
    const originalColors = {
        dashboardBackgroundColor: '#F5F5F5',
        tileBackgroundColor: '#FFFFFF',
        headerBackgroundColor: '#333333',
        dashboardHeaderColor: '#FFFFFF',
        basketBackgroundColor: '#333333',
        basketBackgroundColorInnerCircle: '#333333',
        basketIconColor: '#FFFFFF',
        basketCountBackgroundColor: '#E53E3E',
        basketCountNumberColor: '#FFFFFF',
        fontColor: '#1F2937',
        categoryButtonTextColor: '#333333',
        productDescriptionColor: '#6B7280',
        priceColor: '#333333',
        buttonTextColor: '#FFFFFF'
    };

    // Inline styles for the color component
    const styles = {
        colorInputWrapper: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        },
        colorPicker: {
            width: '50px',
            height: '40px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
        },
        colorPreview: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            fontSize: '14px',
            fontWeight: '500',
            minWidth: '100px',
            textAlign: 'center',
            color: '#333'
        }
    };

    useEffect(() => {
        const get = async () => {
            try {
                setLoader(true);
                const authData = AuthData.getAuthData();
                const url = `${authData.appFunctionUrl}${SETTINGS_API}/colors?code=${authData.azureFunctionKey}`;
                const response = await Fetch(url, authData.clientId);
                setDashboardBackgroundColor(response.dashboardBackgroundColor || '');
                setTileBackgroundColor(response.tileBackgroundColor || '');
                setHeaderBackgroundColor(response.headerBackgroundColor || '');
                setDashboardHeaderColor(response.dashboardHeaderColor || '');
                setBasketBackgroundColor(response.basketBackgroundColor || '');
                setBasketBackgroundColorInnerCircle(response.basketBackgroundColorInnerCircle || '');
                setBasketIconColor(response.basketIconColor || '');
                setBasketCountBackgroundColor(response.basketCountBackgroundColor || '');
                setBasketCountNumberColor(response.basketCountNumberColor || '');
                setFontColor(response.fontColor || '');
                setCategoryButtonTextColor(response.categoryButtonTextColor || '');
                setProductDescriptionColor(response.productDescriptionColor || '');
                setPriceColor(response.priceColor || '');
                setButtonTextColor(response.buttonTextColor || '#FFFFFF');
            } catch (error) {
                console.error("Error fetching colors:", error);
            } finally {
                setLoader(false);
            }
        };

        if (!palette) {
            get();
        }
    }, [setLoader, palette]);

    // Handle color changes and track modifications
    const handleColorChange = (setter, value) => {
        setter(value);
        setModified(true);
        // Notify parent that colors have been manually changed
        if (onColorChange) {
            onColorChange();
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const payload = {
            dashboardBackgroundColor: dashboardBackgroundColor,
            tileBackgroundColor: tileBackgroundColor,
            headerBackgroundColor: headerBackgroundColor,
            dashboardHeaderColor: dashboardHeaderColor,
            basketBackgroundColor: basketBackgroundColor,
            basketBackgroundColorInnerCircle: basketBackgroundColorInnerCircle,
            basketIconColor: basketIconColor,
            basketCountBackgroundColor: basketCountBackgroundColor,
            basketCountNumberColor: basketCountNumberColor,
            fontColor: fontColor,
            categoryButtonTextColor: categoryButtonTextColor,
            productDescriptionColor: productDescriptionColor,
            priceColor: priceColor,
            buttonTextColor: buttonTextColor
        };

        if (onSave) {
            onSave(payload);
        }
        setModified(false);
    };

    // Handle reset confirmation
    const handleYes = () => {
        setDashboardBackgroundColor(originalColors.dashboardBackgroundColor);
        setTileBackgroundColor(originalColors.tileBackgroundColor);
        setHeaderBackgroundColor(originalColors.headerBackgroundColor);
        setDashboardHeaderColor(originalColors.dashboardHeaderColor);
        setBasketBackgroundColor(originalColors.basketBackgroundColor);
        setBasketBackgroundColorInnerCircle(originalColors.basketBackgroundColorInnerCircle);
        setBasketIconColor(originalColors.basketIconColor);
        setBasketCountBackgroundColor(originalColors.basketCountBackgroundColor);
        setBasketCountNumberColor(originalColors.basketCountNumberColor);
        setFontColor(originalColors.fontColor);
        setCategoryButtonTextColor(originalColors.categoryButtonTextColor);
        setProductDescriptionColor(originalColors.productDescriptionColor);
        setPriceColor(originalColors.priceColor);
        setButtonTextColor(originalColors.buttonTextColor);
        setModified(true);
        setShowPopup(false);
        if (setError) setError(null);
        if (setSuccess) setSuccess(null);
    };

    const handleNo = () => {
        setShowPopup(false);
    };

    return (
        <>
            <div className="form-wrapper" style={{ opacity: loader ? 0.3 : 1 }}>
                <form onSubmit={handleSubmit}>
                    <div className="form-grid">

                        <label htmlFor="dashboardHeader" className="form-label">Dashboard Title Color:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="dashboardHeader"
                                value={dashboardHeaderColor}
                                onChange={(e) => handleColorChange(setDashboardHeaderColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: dashboardHeaderColor }}>
                                {dashboardHeaderColor}
                            </span>
                        </div>

                        <label htmlFor="dashboardBg" className="form-label">Dashboard Background Color:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="dashboardBg"
                                value={dashboardBackgroundColor.includes('rgb') ? '#f1f1f6' : dashboardBackgroundColor}
                                onChange={(e) => handleColorChange(setDashboardBackgroundColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: dashboardBackgroundColor }}>
                                {dashboardBackgroundColor}
                            </span>
                        </div>

                        <label htmlFor="tileBg" className="form-label">Tile Background Color:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="tileBg"
                                value={tileBackgroundColor}
                                onChange={(e) => handleColorChange(setTileBackgroundColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: tileBackgroundColor }}>
                                {tileBackgroundColor}
                            </span>
                        </div>

                        <label htmlFor="headerBg" className="form-label">Header Background Color:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="headerBg"
                                value={headerBackgroundColor === 'orange' ? '#ffa500' : headerBackgroundColor}
                                onChange={(e) => handleColorChange(setHeaderBackgroundColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: headerBackgroundColor }}>
                                {headerBackgroundColor}
                            </span>
                        </div>


                        <label htmlFor="basketBg" className="form-label">Basket Background Color:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="basketBg"
                                value={basketBackgroundColor}
                                onChange={(e) => handleColorChange(setBasketBackgroundColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: basketBackgroundColor }}>
                                {basketBackgroundColor}
                            </span>
                        </div>

                        <label htmlFor="basketCountBg" className="form-label">Basket Count Background Color:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="basketCountBg"
                                value={basketCountBackgroundColor}
                                onChange={(e) => handleColorChange(setBasketCountBackgroundColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: basketCountBackgroundColor }}>
                                {basketCountBackgroundColor}
                            </span>
                        </div>

                        <label htmlFor="basketCountNumber" className="form-label">Basket Count Number Color:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="basketCountNumber"
                                value={basketCountNumberColor}
                                onChange={(e) => handleColorChange(setBasketCountNumberColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: basketCountNumberColor }}>
                                {basketCountNumberColor}
                            </span>
                        </div>

                        <label htmlFor="basketInnerCircle" className="form-label">Basket Color Inner Circle:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="basketInnerCircle"
                                value={basketBackgroundColorInnerCircle}
                                onChange={(e) => handleColorChange(setBasketBackgroundColorInnerCircle, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: basketBackgroundColorInnerCircle }}>
                                {basketBackgroundColorInnerCircle}
                            </span>
                        </div>

                        <label htmlFor="basketIcon" className="form-label">Basket Icon Color:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="basketIcon"
                                value={basketIconColor}
                                onChange={(e) => handleColorChange(setBasketIconColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: basketIconColor }}>
                                {basketIconColor}
                            </span>
                        </div>

                        <label htmlFor="fontColor" className="form-label">Form Font Color:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="fontColor"
                                value={fontColor}
                                onChange={(e) => handleColorChange(setFontColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: fontColor, color: '#333' }}>
                                {fontColor}
                            </span>
                        </div>

                        <label htmlFor="categoryButtonTextColor" className="form-label">Category Button Text:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="categoryButtonTextColor"
                                value={categoryButtonTextColor || '#1F2937'}
                                onChange={(e) => handleColorChange(setCategoryButtonTextColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: categoryButtonTextColor || '#1F2937', color: '#fff' }}>
                                {categoryButtonTextColor || '#1F2937'}
                            </span>
                        </div>

                        <label htmlFor="productDescriptionColor" className="form-label">Product Description:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="productDescriptionColor"
                                value={productDescriptionColor || '#6B7280'}
                                onChange={(e) => handleColorChange(setProductDescriptionColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: productDescriptionColor || '#6B7280', color: '#fff' }}>
                                {productDescriptionColor || '#6B7280'}
                            </span>
                        </div>

                        <label htmlFor="priceColor" className="form-label">Price Color:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="priceColor"
                                value={priceColor || '#1F2937'}
                                onChange={(e) => handleColorChange(setPriceColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: priceColor || '#1F2937', color: '#fff' }}>
                                {priceColor || '#1F2937'}
                            </span>
                        </div>

                        <label htmlFor="buttonTextColor" className="form-label">Button Text Color:</label>
                        <div style={styles.colorInputWrapper} className="input-field">
                            <input
                                type="color"
                                id="buttonTextColor"
                                value={buttonTextColor || '#FFFFFF'}
                                onChange={(e) => handleColorChange(setButtonTextColor, e.target.value)}
                                style={styles.colorPicker}
                            />
                            <span style={{ ...styles.colorPreview, backgroundColor: buttonTextColor || '#FFFFFF', color: '#333' }}>
                                {buttonTextColor || '#FFFFFF'}
                            </span>
                        </div>
                    </div>
                    <div className='form-footer'>
                        <button
                            type="submit"
                            disabled={!modified}
                            className="submit-button"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            className='clear-data-button'
                            style={{ marginLeft: '10px' }}
                            onClick={() => setShowPopup(true)}
                        >
                            Reset
                        </button>
                    </div>
                </form>
            </div>
            {showPopup && (
                <YesNoPopup
                    handleYes={handleYes}
                    handleNo={handleNo}
                    message="Are you sure you want to restore colors to default values?"
                />
            )}
        </>
    );
}

export default AdvancedColors;
